/**
 * Video processing utilities for thumbnail generation and metadata extraction
 */

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
}

/**
 * Extract video metadata (duration, dimensions) from a video file
 */
export const getVideoMetadata = (file: File): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      URL.revokeObjectURL(video.src);
    };
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Generate a thumbnail image from a video file at a specific time (default: 1 second)
 * Returns a JPEG Blob
 */
export const generateVideoThumbnail = (
  file: File, 
  seekTime: number = 1, 
  maxWidth: number = 400,
  quality: number = 0.85
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    let hasResolved = false;
    
    const cleanup = () => {
      URL.revokeObjectURL(video.src);
    };
    
    video.onloadedmetadata = () => {
      // Seek to the specified time, or use 0 if video is shorter
      const targetTime = Math.min(seekTime, video.duration * 0.1);
      video.currentTime = targetTime;
    };
    
    video.onseeked = () => {
      if (hasResolved) return;
      hasResolved = true;
      
      try {
        // Calculate scaled dimensions
        const aspectRatio = video.videoWidth / video.videoHeight;
        let width = Math.min(video.videoWidth, maxWidth);
        let height = width / aspectRatio;
        
        // Create canvas and draw the frame
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(video, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    
    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video for thumbnail generation'));
    };
    
    // Set timeout for videos that may not emit seeked event properly
    setTimeout(() => {
      if (!hasResolved) {
        cleanup();
        reject(new Error('Thumbnail generation timed out'));
      }
    }, 10000);
    
    video.src = URL.createObjectURL(file);
    video.load();
  });
};

/**
 * Calculate display size for video player while maintaining aspect ratio
 */
export const calculateVideoDisplaySize = (
  videoWidth: number, 
  videoHeight: number,
  maxWidth: number = 400,
  maxHeight: number = 300
): { width: number; height: number; aspectRatio: number } => {
  const aspectRatio = videoWidth / videoHeight;
  
  let displayWidth = videoWidth;
  let displayHeight = videoHeight;
  
  // Scale down if too wide
  if (displayWidth > maxWidth) {
    displayWidth = maxWidth;
    displayHeight = displayWidth / aspectRatio;
  }
  
  // Scale down if still too tall
  if (displayHeight > maxHeight) {
    displayHeight = maxHeight;
    displayWidth = displayHeight * aspectRatio;
  }
  
  return { 
    width: Math.round(displayWidth), 
    height: Math.round(displayHeight),
    aspectRatio 
  };
};

/**
 * Check if a MIME type is a video type
 */
export const isVideoType = (mimeType: string): boolean => {
  return mimeType.startsWith('video/');
};

/**
 * Supported video MIME types
 */
export const VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/mpeg',
];
